import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { jobsApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { Job } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

// Query keys
export const jobKeys = {
    all: ['jobs'] as const,
    lists: () => [...jobKeys.all, 'list'] as const,
    list: (params: ListParams) => [...jobKeys.lists(), params] as const,
    details: () => [...jobKeys.all, 'detail'] as const,
    detail: (id: number) => [...jobKeys.details(), id] as const,
    runs: (id: number) => [...jobKeys.detail(id), 'runs'] as const,
    runList: (id: number, params: ListParams) => [...jobKeys.runs(id), params] as const,
}

// List jobs
export function useJobs(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: jobKeys.list(params || {}),
        queryFn: () => jobsApi.list(params),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired,
    })
}

// Get single job
export function useJob(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: jobKeys.detail(id),
        queryFn: () => jobsApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

// Get job runs
export function useJobRuns(id: number, params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: jobKeys.runList(id, params || {}),
        queryFn: () => jobsApi.getRuns(id, params),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

// Create job
export function useCreateJob() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<Job, 'id' | 'createdAt' | 'updatedAt' | 'lastRun' | 'nextRun' | 'status'>) =>
            jobsApi.create(data),
        onSuccess: (_response) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
            toast({
                title: 'Job Created',
                description: `Job has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Update job
export function useUpdateJob() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Job, 'id' | 'createdAt' | 'updatedAt' | 'lastRun' | 'nextRun' | 'status'>> }) =>
            jobsApi.update(id, data),
        onSuccess: (_response, _variables) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(_variables.id) })
            toast({
                title: 'Job Updated',
                description: `Job has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Delete job
export function useDeleteJob() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => jobsApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
            queryClient.removeQueries({ queryKey: jobKeys.detail(id) })
            toast({
                title: 'Job Deleted',
                description: 'Job has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Run job
export function useRunJob() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => jobsApi.run(id),
        onSuccess: (_response, _id) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(_id) })
            queryClient.invalidateQueries({ queryKey: jobKeys.runs(_id) })
            toast({
                title: 'Job Started',
                description: 'Job execution has been started.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Pause job
export function usePauseJob() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => jobsApi.pause(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) })
            queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
            toast({
                title: 'Job Paused',
                description: 'Job has been paused successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Resume job
export function useResumeJob() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => jobsApi.resume(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) })
            queryClient.invalidateQueries({ queryKey: jobKeys.lists() })
            toast({
                title: 'Job Resumed',
                description: 'Job has been resumed successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}



